import { useEffect, useState } from 'react';
import { useAuthFetch } from '@/hooks';
import styles from './AuthImage.module.css';

interface AuthImageProps {
  src:        string;   // relative API path, e.g. /conversations/123/attachments/456
  alt:        string;
  className?: string;
  onLoad?:    () => void;
  onClick?:   (blobUrl: string) => void;  // passes blob URL so lightbox can reuse it
}

/**
 * Module-level cache of src → blob URL.
 * Persists across re-renders and poll-triggered refetches so that images that
 * have already been fetched are never re-downloaded.  Blob URLs are permanent
 * for the lifetime of the page (we never revoke them, which is fine for the
 * small number of medical images in a conversation).
 */
const blobCache = new Map<string, string>();

export function AuthImage({ src, alt, className, onLoad, onClick }: AuthImageProps) {
  const authFetch             = useAuthFetch();
  const [blobUrl, setBlobUrl] = useState<string | null>(() => blobCache.get(src) ?? null);

  useEffect(() => {
    // Already cached — nothing to do.
    if (blobCache.has(src)) {
      setBlobUrl(blobCache.get(src)!);
      return;
    }

    let cancelled = false;

    authFetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        blobCache.set(src, objectUrl);
        setBlobUrl(objectUrl);
      })
      .catch(() => { /* silently fail — img won't render */ });

    return () => { cancelled = true; };
  }, [src, authFetch]);

  if (!blobUrl) return <div className={`${styles.skeleton} ${className ?? ''}`} aria-hidden="true" />;

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      onLoad={onLoad}
      onClick={onClick ? () => onClick(blobUrl) : undefined}
    />
  );
}
